<script setup lang="ts">
  import { nextTick, ref, watch } from 'vue';

  import {
    TagsInput,
    TagsInputInput,
    TagsInputItem,
    TagsInputItemText,
  } from '@ui/components/tags-input';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn, isEmailValid, isValidDomain } from '@ui/utils/utils';

  interface EmailInputContact {
    email: string;
    name: string | null;
  }

  const props = defineProps<{
    modelValue: string[];
    single?: boolean;
    disabled?: boolean;
    domainOnly?: boolean;
    placeholder?: string;
    suggestedContacts?: EmailInputContact[];
  }>();

  const emits = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
    (e: 'focus'): void;
    (e: 'blur'): void;
  }>();

  const internalValue = ref<string[]>(props.modelValue ?? []);
  const selectedEmail = ref<string | null>(null);
  const inputContainer = ref<HTMLElement | null>(null);

  const validate = (email: string) =>
    props.domainOnly ? isValidDomain(email) : isEmailValid(email);

  const focusInput = () => {
    nextTick(() => {
      inputContainer.value?.querySelector('input')?.focus();
    });
  };

  const onBlur = (e: FocusEvent) => {
    parseEmail(e);
    selectedEmail.value = null;
    emits('blur');
  };

  const parseEmail = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (props.single && internalValue.value.length > 0) {
      input.value = '';
      return;
    }
    const newValue = input.value.trim();
    if (newValue !== '' && !internalValue.value.includes(newValue)) {
      addEmail(newValue);
    }
    input.value = '';
  };

  const addEmail = (email: string) => {
    const updatedValue = [...internalValue.value, email];
    emits('update:modelValue', updatedValue.filter(validate));
    internalValue.value = updatedValue;
  };

  const removeEmail = (email: string) => {
    const updatedValue = internalValue.value.filter((v) => v !== email);
    emits('update:modelValue', updatedValue.filter(validate));
    internalValue.value = updatedValue;
  };

  // Handles Reka's native tag deletion (backspace when nothing is selected in our state)
  const onTagsUpdate = (val) => {
    const strings = (val ?? []).map(String);
    internalValue.value = strings;
    emits('update:modelValue', strings.filter(validate));
  };

  const onTagClick = (e: MouseEvent, email: string) => {
    if ((e.target as HTMLElement).closest('button')) return;
    selectedEmail.value = email;
    focusInput();
  };

  const onInputKeydown = (e: KeyboardEvent) => {
    const input = e.target as HTMLInputElement;

    if (e.key === 'ArrowLeft' && input.selectionStart === 0) {
      e.preventDefault();
      if (selectedEmail.value) {
        const idx = internalValue.value.indexOf(selectedEmail.value);
        selectedEmail.value = internalValue.value[Math.max(0, idx - 1)];
      } else {
        selectedEmail.value = internalValue.value.at(-1) ?? null;
      }
      return;
    }

    if (e.key === 'ArrowRight' && selectedEmail.value) {
      e.preventDefault();
      const idx = internalValue.value.indexOf(selectedEmail.value);
      selectedEmail.value =
        idx < internalValue.value.length - 1 ? internalValue.value[idx + 1] : null;
      return;
    }

    if (e.key === 'Backspace' && input.value === '' && selectedEmail.value) {
      e.preventDefault();
      removeEmail(selectedEmail.value);
      selectedEmail.value = null;
      return;
    }

    if (!selectedEmail.value) return;
    if (!(e.metaKey || e.ctrlKey) && e.key !== 'Meta' && e.key !== 'Control') {
      selectedEmail.value = null;
      return;
    }
    if (e.key === 'c') {
      e.preventDefault();
      navigator.clipboard.writeText(selectedEmail.value);
    } else if (e.key === 'x') {
      e.preventDefault();
      navigator.clipboard.writeText(selectedEmail.value);
      removeEmail(selectedEmail.value);
      selectedEmail.value = null;
    }
  };

  const onInputPaste = (e: ClipboardEvent) => {
    if (props.disabled) return;
    const text = e.clipboardData?.getData('text/plain')?.trim();
    if (!text) return;
    e.preventDefault();
    const emails = text
      .split(/[,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    for (const eml of emails) {
      if (validate(eml) && !internalValue.value.includes(eml)) {
        addEmail(eml);
      }
    }
  };

  watch(
    () => props.modelValue,
    (newVal) => {
      internalValue.value = newVal ?? [];
    },
  );
</script>

<template>
  <TagsInput
    :model-value="internalValue"
    class="w-full"
    :disabled="disabled"
    @update:model-value="onTagsUpdate"
  >
    <TagsInputItem
      v-for="item in internalValue"
      :key="item"
      :value="item"
      :disabled="disabled"
      :class="
        cn(
          !disabled && 'cursor-pointer',
          selectedEmail === item && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
        )
      "
      @click="(e: MouseEvent) => onTagClick(e, item)"
    >
      <Tooltip :disabled="validate(item)">
        <TooltipTrigger as-child>
          <TagsInputItemText
            :class="cn('text-xs px-2 truncate', !validate(item) && 'text-destructive')"
          />
          <slot name="emailActions" :email="item" />
        </TooltipTrigger>
        <TooltipContent>Invalid email</TooltipContent>
      </Tooltip>
    </TagsInputItem>

    <template v-if="!internalValue.length && suggestedContacts">
      <TagsInputItem
        v-for="item in suggestedContacts.slice(0, 1)"
        :key="item.email"
        class="border border-dashed bg-transparent text-muted-foreground hover:border-solid hover:text-foreground"
        :value="item.email"
        :disabled="disabled"
      >
        <Tooltip>
          <TooltipTrigger as-child>
            <button type="button" class="py-1 px-2" @click="() => addEmail(item.email)">
              <span class="text-2xs font-semibold">Maybe:</span>
              <TagsInputItemText class="text-xs truncate" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Suggested based on customer contact:
            <span class="font-semibold">{{ item.name }}</span>
          </TooltipContent>
        </Tooltip>
      </TagsInputItem>
    </template>

    <div ref="inputContainer" style="display: contents">
      <TagsInputInput
        :placeholder="placeholder"
        @keydown.space="parseEmail"
        @keydown.enter="parseEmail"
        @keydown="onInputKeydown"
        @paste="onInputPaste"
        @focus="emits('focus')"
        @blur="onBlur"
      />
    </div>
    <slot name="suggestions" />
    <slot />
  </TagsInput>
</template>
